import Invoice from "../../entities/invoice";
import IconButton from "../../ui/IconButton/IconButton";
import Icons from "../../ui/IconButton/Icons";
import i18n from "../../i18n/i18n";
import FinancialHelper from "../FinancialHelper";
import {ReactNode} from "react";
import Transaction from "../../entities/transaction";

/**
 * FR is Fund Retainer
 * FO is not used
 * PT is Payment from Trust
 * PC is Payment from Client
 */
export const PAYMENTS = ['FR', 'FO', 'PT', 'PC'];
/**
 * RR is Refund Retainer (from Trust)
 * RO is Refund Retainer (from Operating) - happens when we overcharged
 * DT is Disburse from Trust
 */
export const WITHDRAWALS = ['RR', 'RO', 'DT'];
/**
 * TT is Transfer from Trust to Trust
 */
export const TRANSFERS = ['TT'];
/**
 * BA is Balance Adjustment
 * ST is Starting amount for a Trust
 * SA is Starting Amount for a case
 */
export const BALANCE_ADJUSTMENTS = ['BA', 'ST', 'SA'];
/**
 * ED is not used
 * DE is not used
 */
export const ESCROWS = ['ED', 'DE'];

/**
 * Check if this is a payment type
 * @param {string} code
 * @returns {boolean}
 */
export function isPayment(code: string): boolean {
    return PAYMENTS.includes(code);
}

/**
 * Check if this is a withdrawal type
 * @param {string} code
 * @returns {boolean}
 */
export function isWithdrawal(code: string): boolean {
    return WITHDRAWALS.includes(code);
}

/**
 * Check if this is a transfer type
 * @param {string} code
 * @returns {boolean}
 */
export function isTransfer(code: string): boolean {
    return (code === 'TT');
}

/**
 * Check if this is a balance adjustment type
 * @param {string} code
 * @returns {boolean}
 */
export function isBalanceAdjustment(code: string): boolean {
    return BALANCE_ADJUSTMENTS.includes(code);
}

/**
 * Check if this is an escrow type
 * @param {string} code
 * @returns {boolean}
 */
export function isEscrow(code: string): boolean {
    return ESCROWS.includes(code);
}

/**
 * Helper function to get the proper label for the buttons.
 *
 * @param {string} code - code to apply (stored in i18n)
 * @param {number} width - width of the current container
 * @param {boolean} [isExport] - are we being called during an export?
 * @returns {string}
 */
export function setup(code: string, width: number, isExport?: boolean): string{
    const minWidth = 370;
    const maxWidth = 580;

    return width > maxWidth || isExport
           ? i18n.ls('transaction', code)
           : width < minWidth
             ? i18n.ls('transaction', `${code}code`)
             : i18n.ls('transaction', `${code}mini`);
}

/**
 * Get the transaction type label.
 *
 * @param {Transaction|Invoice} tr - the transaction (or invoice) to encode
 * @param {number} width - width of the containing component
 * @param {boolean} [isExport] - is this being exported to a file?
 * @return {JSX.Element|string}
 */
export function renderTransactionType(tr: Transaction | Invoice, width: number, isExport: boolean): ReactNode {
    if (!tr) return null;

    const isInvoice = tr instanceof Invoice;

    let type = '';
    let iconButton = undefined;
    let color = undefined;
    let onClick = undefined;

    const fund = setup('F', width, isExport);
    const payment = setup('P', width, isExport);
    const refund = setup('R', width, isExport);
    const withdrawal = setup('W', width, isExport);
    const transfer = setup('T', width, isExport);
    const escrow = setup('E', width, isExport);
    const adjustment = setup('A', width, isExport);
    const accountBalance = setup('BA', width, isExport);
    const trustBalance = setup('ST', width, isExport);
    let invoice = setup('I', width, isExport);

    if (isInvoice) {
        iconButton = Icons.INVOICE;
        type = invoice;
        color = tr.invoiceStatus === 'F' ? 'darkgreen' : 'orange';
        onClick = () => this.onShowInvoice(FinancialHelper.getInvoiceById(tr.invoiceId));
    }
    else if (PAYMENTS.includes(tr.txType)) {
        iconButton = Icons.PAYMENT;
        type = tr.txType === 'FR' ? fund : payment;
    }
    else if (WITHDRAWALS.includes(tr.txType)) {
        iconButton = Icons.WITHDRAW_FUNDS;
        type = ['RR', 'RO'].includes(tr.txType) ? refund : withdrawal;
    }
    else if (TRANSFERS.includes(tr.txType)) {
        iconButton = Icons.TRANSFER_FUNDS;
        type = transfer;
    }
    else if (BALANCE_ADJUSTMENTS.includes(tr.txType)) {
        iconButton = Icons.BALANCE_ADJUSTMENT;
        type = tr.txType === 'BA' ? adjustment : tr.txType === 'ST' ? trustBalance : accountBalance;
        // type = adjustment;
    }
    else if (ESCROWS.includes(tr.txType)) {
        iconButton = Icons.ESCROW;
        type = escrow;
    }
    else throw new Error(i18n.ls('transaction', 'errUnknownTxType', {type: tr.txType}));

    if (isExport === true) return type;

    const label = type;

    return <IconButton key={`${tr.id}-tt`}
                       type={iconButton}
                       color={color}
                       label={width > 550 ? label : null}
                       onClick={onClick}
                       clickPassthrough={false}
    />;
}